import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import {
  Alert,
  Button,
  CloseButton,
  Container,
  Loader,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core"
import { IconSearch, IconSearchOff } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { EmptyState } from "../../../../components/EmptyState"
import { SearchFilters } from "../../../../components/SearchFilters"
import { SearchHitCard } from "../../../../components/SearchHitCard"
import { VoxPageTitle } from "../../../../components/VoxPageTitle"
import { formatInteger, formatShortDate } from "../../../../lib/format-date"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import { type ITopicMeetingLink, presentSearchHits, type TSearchSort } from "../present-hits"
import type { IViewProps } from "./interfaces"

import classes from "./styles.module.css"

/** Placeholder cards while the engine loads / warms — honest shape, not a bare
 * spinner, so the layout doesn't jump when real results arrive. */
const TopicGroupRow = ({ label, meetings }: { label: string; meetings: ITopicMeetingLink[] }): React.JSX.Element => {
  const { i18n } = useLingui()
  return (
    <div className={classes.topicGroup} data-testid="topic-group">
      <p className={classes.topicSummary}>
        <Trans>Topic</Trans>
        {" · "}
        {label}
        {" · "}
        <Plural value={meetings.length} one="in # meeting" other="in # meetings" />
      </p>
      <ul className={classes.topicMeetings}>
        {meetings.map((meeting) => (
          <li key={meeting.meetingId}>
            <Link to="/meetings/$id" params={{ id: meeting.meetingId }} className={classes.topicLink}>
              <span>{displayMeetingTitle(meeting.subject, null, i18n)}</span>
              <span className={classes.topicDate}>{formatShortDate(i18n, meeting.date)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

const SkeletonCards = (): React.JSX.Element => (
  <Stack gap="sm">
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} height={104} radius="md" />
    ))}
  </Stack>
)

/**
 * Search page — pure presentation. The smart route wrapper owns the debounced
 * query, the API call, warming retries and the URL filter state; this view
 * renders the search box, filter bar and the right body for `viewState`.
 */
export const SearchView = ({
  product,
  query,
  onQueryChange,
  filters,
  viewState,
  hits,
  sort,
  onSortChange,
  engine,
  errorMessage,
  onRetry,
}: IViewProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const rows = presentSearchHits(hits, sort)

  const body = ((): React.JSX.Element => {
    switch (viewState) {
      case "prequery":
        return (
          <EmptyState
            icon={<IconSearch size={28} />}
            tone="brand"
            title={<Trans>Search across your meetings</Trans>}
            description={
              <Trans>
                Find anything said, decided or asked — transcript turns, decisions, action points, open questions and
                more. Start typing above.
              </Trans>
            }
          />
        )
      case "loading":
        return <SkeletonCards />
      case "warming":
      case "warming-long":
        return (
          <Stack gap="md">
            <Alert
              variant="light"
              color="brand"
              icon={<Loader size="sm" color="brand" />}
              title={<Trans>Warming your search index…</Trans>}
            >
              {viewState === "warming-long" ? (
                <Trans>This is taking longer than usual — hang tight, we'll show results as soon as it's ready.</Trans>
              ) : (
                <Trans>First search in a while — this only takes a moment.</Trans>
              )}
            </Alert>
            <SkeletonCards />
          </Stack>
        )
      case "unavailable":
        return (
          <EmptyState
            icon={<IconSearchOff size={28} />}
            tone="danger"
            title={<Trans>Search is unavailable</Trans>}
            description={
              <Trans>
                The search engine can't be reached right now. This is usually temporary — try again shortly.
              </Trans>
            }
            action={
              <Button variant="light" color="danger" onClick={onRetry}>
                <Trans>Try again</Trans>
              </Button>
            }
          />
        )
      case "error":
        return (
          <EmptyState
            icon={<IconSearchOff size={28} />}
            tone="danger"
            title={<Trans>Something went wrong</Trans>}
            description={errorMessage ?? <Trans>The search couldn't be completed.</Trans>}
            action={
              <Button variant="light" color="danger" onClick={onRetry}>
                <Trans>Try again</Trans>
              </Button>
            }
          />
        )
      case "results":
        if (hits.length === 0) {
          return (
            <EmptyState
              icon={<IconSearchOff size={28} />}
              tone="neutral"
              title={<Trans>No results</Trans>}
              description={<Trans>Nothing matched your search. Try different words, or widen the filters above.</Trans>}
            />
          )
        }
        return (
          <Stack gap="sm">
            <div className={classes.resultsHead}>
              <Text size="sm" fw={600}>
                <Plural value={rows.length} one="# result" other="# results" />
              </Text>
              {engine && (
                <Text size="xs" c="dimmed">
                  <Trans>searched {formatInteger(i18n, engine.chunkCount)} passages</Trans>
                </Text>
              )}
              <SegmentedControl
                className={classes.sort}
                size="xs"
                value={sort}
                onChange={(value) => onSortChange(value === "newest" ? "newest" : "relevance")}
                data={
                  [
                    { label: i18n._(msg`Relevance`), value: "relevance" },
                    { label: i18n._(msg`Newest`), value: "newest" },
                  ] satisfies { label: string; value: TSearchSort }[]
                }
                aria-label={i18n._(msg`Sort results`)}
              />
            </div>
            <div className={classes.resultsList}>
              {rows.map((row) =>
                row.kind === "topics" ? (
                  <TopicGroupRow key={`topics:${row.label}`} label={row.label} meetings={row.meetings} />
                ) : (
                  <SearchHitCard key={row.hit.chunkId} hit={row.hit} query={query} />
                ),
              )}
            </div>
          </Stack>
        )
    }
  })()

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {product === "voxcanon" ? (
          <VoxPageTitle
            eyebrow={{ primary: i18n._(msg`Workspace`), secondary: i18n._(msg`Recall`) }}
            leading={<Trans>Search the</Trans>}
            accent={<Trans>chronicle</Trans>}
            subline={<Trans>Find any moment across your sessions — spoken, decided or asked.</Trans>}
          />
        ) : (
          <div>
            <Title order={2}>
              <Trans>Search</Trans>
            </Title>
            <Text c="dimmed" size="sm">
              <Trans>Search across every meeting your team can see.</Trans>
            </Text>
          </div>
        )}

        <TextInput
          size="md"
          value={query}
          onChange={(e) => onQueryChange(e.currentTarget.value)}
          leftSection={<IconSearch size={18} />}
          rightSection={
            query.length > 0 ? (
              <CloseButton aria-label={i18n._(msg`Clear search`)} onClick={() => onQueryChange("")} />
            ) : null
          }
          placeholder={i18n._(msg`Search meetings, decisions, action points…`)}
          aria-label={i18n._(msg`Search`)}
        />

        <SearchFilters {...filters} />

        <div className={classes.body}>{body}</div>
      </Stack>
    </Container>
  )
}
